import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { CopyIcon, ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { deleteField, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";

const WEBHOOK_URL = "https://northamerica-northeast1-fiverrrms1.cloudfunctions.net/uberEatsOrderWebhook";
const SECRET_DOC = doc(db, "eatPrinterSecrets", "master");
const SECRET_PATTERN = /^[0-9a-fA-F]{64}$/;

const EatPrinterSettings: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [savedSecret, setSavedSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [stage, setStage] = useState<"locked" | "confirm" | "paste">("locked");
  const [draft, setDraft] = useState("");
  const [removeWord, setRemoveWord] = useState("");
  const [saving, setSaving] = useState(false);
  const panelBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("#d5e4ea", "gray.600");

  useEffect(() => {
    let cancelled = false;
    getDoc(SECRET_DOC)
      .then((snap) => {
        if (cancelled) return;
        const secret = snap.exists() ? snap.data()?.masterWebhookSecret : "";
        setSavedSecret(typeof secret === "string" ? secret : "");
        setShowSecret(false);
      })
      .catch((error) => {
        console.error("Eat Printer settings could not be loaded", error);
        toast({ title: "Could not load Eat Printer settings", status: "error", duration: 3000 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const closeEditor = () => {
    setDraft("");
    setRemoveWord("");
    setStage("locked");
  };

  const saveSecret = async () => {
    const nextSecret = draft.trim();
    if (!SECRET_PATTERN.test(nextSecret)) {
      toast({
        title: "Enter the 64-character master webhook secret.",
        status: "error",
        duration: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      await setDoc(SECRET_DOC, {
        masterWebhookSecret: nextSecret,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSavedSecret(nextSecret);
      setShowSecret(false);
      closeEditor();
      toast({ title: "Master webhook secret saved", status: "success", duration: 2500 });
    } catch (error) {
      console.error("Eat Printer secret could not be saved", error);
      toast({ title: "Could not save the master webhook secret", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const removeSecret = async () => {
    if (removeWord !== "REMOVE") return;
    setSaving(true);
    try {
      await setDoc(SECRET_DOC, {
        masterWebhookSecret: deleteField(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSavedSecret("");
      setShowSecret(false);
      closeEditor();
      toast({ title: "Master webhook secret removed", status: "success", duration: 2500 });
    } catch (error) {
      console.error("Eat Printer secret could not be removed", error);
      toast({ title: "Could not remove the master webhook secret", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const copyAddress = () => {
    navigator.clipboard.writeText(WEBHOOK_URL);
    toast({ title: "Webhook address copied", status: "success", duration: 2000 });
  };

  return (
    <PageShell
      title="Eat Printer"
      description="Copy the Uber Eats webhook address. Paste the master webhook secret used to confirm that a webhook came from Eat Printer."
    >
      <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={6} mb={4}>
        <FormControl>
          <FormLabel mb={1}>Webhook address</FormLabel>
          <Text fontSize="sm" color="gray.600" mb={3}>
            Copy this address into Eat Printer. It cannot be changed.
          </Text>
          <HStack align="center">
            <Text
              flex="1"
              fontFamily="mono"
              fontSize="sm"
              wordBreak="break-all"
              border="1px solid"
              borderColor={borderColor}
              borderRadius="md"
              px={3}
              py={2}
            >
              {WEBHOOK_URL}
            </Text>
            <Button minH="44px" flexShrink={0} leftIcon={<CopyIcon />} onClick={copyAddress}>
              Copy
            </Button>
          </HStack>
        </FormControl>
      </Box>
      <Box bg={panelBg} border="1px solid" borderColor={borderColor} borderRadius="lg" p={6}>
        <VStack align="stretch" spacing={4}>
          <FormControl>
            <FormLabel mb={1}>Master webhook secret</FormLabel>
            <Text fontSize="sm" color="gray.600">
              The secret is 64 hexadecimal characters. Click the eye to show the saved secret. A store uses this secret when that store has no webhook secret of its own.
            </Text>
          </FormControl>
          {loading ? (
            <Text>Loading</Text>
          ) : savedSecret ? (
            <HStack align="center">
              <Input
                type={showSecret ? "text" : "password"}
                value={savedSecret}
                isReadOnly
                fontFamily="mono"
                autoComplete="off"
              />
              <IconButton
                aria-label={showSecret ? "Hide secret" : "Show secret"}
                icon={showSecret ? <ViewOffIcon /> : <ViewIcon />}
                minH="44px"
                minW="44px"
                variant="outline"
                onClick={() => setShowSecret((visible) => !visible)}
              />
              {stage === "locked" && (
                <Button minH="44px" variant="outline" onClick={() => setStage("confirm")}>
                  Edit
                </Button>
              )}
            </HStack>
          ) : (
            <HStack justify="space-between">
              <Text>No master webhook secret is saved.</Text>
              {stage === "locked" && (
                <Button minH="44px" variant="outline" onClick={() => setStage("confirm")}>
                  Edit
                </Button>
              )}
            </HStack>
          )}
          {stage === "confirm" && (
            <VStack align="stretch" spacing={3}>
              <Text fontSize="sm">
                Replace the master webhook secret? Stores without their own secret will reject Uber Eats webhooks until Eat Printer uses the new secret.
              </Text>
              <HStack>
                <Button minH="44px" onClick={() => setStage("paste")}>Continue</Button>
                <Button minH="44px" variant="outline" onClick={closeEditor}>Cancel</Button>
              </HStack>
            </VStack>
          )}
          {stage === "paste" && (
            <VStack align="stretch" spacing={3}>
              <Input
                type="password"
                autoComplete="new-password"
                value={draft}
                placeholder="Paste the 64-character secret"
                onChange={(event) => setDraft(event.target.value)}
              />
              <HStack>
                <Button minH="44px" isLoading={saving} onClick={saveSecret}>Save secret</Button>
                <Button minH="44px" variant="outline" isDisabled={saving} onClick={closeEditor}>Cancel</Button>
              </HStack>
              {savedSecret && (
                <VStack align="stretch" spacing={2}>
                  <FormLabel mb={0}>Type REMOVE to delete the saved secret.</FormLabel>
                  <HStack>
                    <Input autoComplete="off" value={removeWord} onChange={(event) => setRemoveWord(event.target.value)} />
                    <Button minH="44px" variant="outline" isDisabled={saving || removeWord !== "REMOVE"} onClick={removeSecret}>
                      Remove secret
                    </Button>
                  </HStack>
                </VStack>
              )}
            </VStack>
          )}
        </VStack>
      </Box>
    </PageShell>
  );
};

export default EatPrinterSettings;
